import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useSwal } from '../hooks/useSwal';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTh } from '../components/SortableTh';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { fuzzyStatusTone as statusTone, fuzzyPriorityTone as priorityTone } from '../constants/tone';

type ExtraColumn = {
  header: string;
  width?: string;
  render: (item: Item) => React.ReactNode;
  /** Optional — provide a raw comparable value to make this column sortable
   * (the header gets a click handler + sort arrow, same as the built-in columns). */
  sortValue?: (item: Item) => string | number;
};

/** One tile in the stat row above the table. */
export type StatCard = {
  label: string;
  value: number | string;
  /** RemixIcon class, e.g. "ri-list-check-2" */
  icon: string;
  /** np-tone-* suffix: success | warning | danger | info | neutral | primary | dark */
  tone?: string;
};

/**
 * A caller-supplied filter control for the toolbar. The caller owns the control
 * and its state; `predicate` is applied to every item to decide what stays
 * visible (omit it for a control that filters nothing by itself).
 */
export type ExtraFilter = {
  /** Stable key for React. */
  id: string;
  /** The control itself — rendered in the toolbar after the status filter. */
  control: React.ReactNode;
  predicate?: (item: Item) => boolean;
};

type Props = {
  title: string;
  iconClass: string;
  listPath: string;
  detailPathPrefix: string;
  configPath?: string;
  configButtonLabel?: string;
  registersPath?: string;
  registersButtonLabel?: string;
  idField: string;
  titleField: string;
  statusField?: string;
  priorityField?: string;
  registerQueryParam?: string; // default: registerId
  extraColumns?: ExtraColumn[];
  rowActions?: (id: string, title: string, registerId: string) => React.ReactNode;
  /**
   * Replace the built-in stat tiles (Total / Open-Active / High-Critical / Closed).
   * Those are derived by keyword-matching the status and priority values, which
   * only works for registers using the Open/Closed + High/Critical vocabulary —
   * any other vocabulary should supply its own tiles here. Receives every fetched
   * item, unfiltered. Return [] to hide the tile row entirely.
   */
  statCards?: (items: Item[]) => StatCard[];
  /** Additional filter controls for the toolbar, rendered after the status filter. */
  extraFilters?: ExtraFilter[];
  /** Extra buttons appended to the page header, after New and the registers link.
   *  Generic extension point — a caller supplies its own actions (e.g. an export
   *  or report link) without this component knowing what kind of register it is. */
  headerActions?: React.ReactNode;
  /**
   * Override how a status value is turned into a chip — used for both the table
   * cell and the status-filter popover. Registers whose statuses are codes
   * (rather than display text) use this to map code -> label + tone; without it
   * the raw value is shown with a keyword-matched tone.
   */
  statusChip?: (status: string) => React.ReactNode;
};

export type Item = {
  [key: string]: any;
  fields?: Record<string, any>;
};

const getValue = (item: Item, fieldId: string): string => {
  const fromFields = item.fields?.[fieldId];
  const direct = (item as any)[fieldId];
  let val = fromFields ?? direct;
  
  // Sanitize react-select objects {value, label} to extract the value
  if (val && typeof val === 'object' && 'value' in val) {
    val = val.value;
  }
  
  if (val === undefined || val === null) return '';
  return String(val);
};

/** The list fetch + sanitize-transform every RegisterItemList uses, extracted so a sibling
 * component (e.g. AiUseCaseList.tsx's Pending AI Use Cases panel) can read the SAME query -
 * TanStack Query dedupes by queryKey across independently-mounted useQuery observers, so both
 * components share one underlying fetch/cache entry rather than issuing two requests that could
 * drift out of sync with each other. */
export function useRegisterItems(listPath: string, registerId: string = '', registerQueryParam: string = 'registerId') {
  return useQuery<Item[]>({
    queryKey: ['list', listPath, registerId],
    queryFn: async () => {
      const url = registerId ? `${listPath}?${registerQueryParam}=${encodeURIComponent(registerId)}` : listPath;
      const response = await api.get<any>(url);

      // Common response shapes: { incidents: [] }, { issues: [] }, etc.
      const firstArrayProp = Object.values(response).find((v) => Array.isArray(v)) as Item[] | undefined;
      return firstArrayProp || [];
    },
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);

        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }

        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };

      return sanitizeObject(data);
    },
  });
}

export const RegisterItemList: React.FC<Props> = ({
  title,
  iconClass,
  listPath,
  detailPathPrefix,
  configPath,
  configButtonLabel = 'Configure Fields',
  registersPath,
  registersButtonLabel = 'Manage Registers',
  idField,
  titleField,
  statusField,
  priorityField,
  registerQueryParam = 'registerId',
  extraColumns = [],
  rowActions,
  statCards,
  extraFilters = [],
  statusChip,
  headerActions,
}) => {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const registerId = searchParams.get(registerQueryParam) || '';
  const swal = useSwal();
  const toast = useToast();
  const [search, setSearch] = useState('');

  // Which status values to hide from the list - starts empty (nothing hidden),
  // and only the ones present in the actual data (with a nice label) are offered.
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const toggleHiddenStatus = (status: string) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  // A number here means "sort by extraColumns[that index]" rather than one of
  // the built-in fields — lets sortable extra columns (e.g. Inherent/Residual
  // Risk) reuse the same sort state/UI instead of needing their own.
  type SortField = 'id' | 'rowTitle' | 'status' | 'priority' | number;

  const { data: items = [], isLoading, error } = useRegisterItems(listPath, registerId, registerQueryParam);

  // Every distinct status value actually present in the data, in first-seen order -
  // this is what populates the status filter dropdown. Computed from ALL items
  // (not the filtered/hidden set) so a status doesn't disappear from the list of
  // options just because it's currently hidden.
  const availableStatuses = useMemo(() => {
    if (!statusField) return [];
    const seen: string[] = [];
    items.forEach((item) => {
      const s = getValue(item, statusField);
      if (s && !seen.includes(s)) seen.push(s);
    });
    return seen;
  }, [items, statusField]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return items
      .map((item) => {
        const id = getValue(item, idField);
        const rowTitle = getValue(item, titleField) || id;
        const status = statusField ? getValue(item, statusField) : '';
        const priority = priorityField ? getValue(item, priorityField) : '';
        const extra = extraColumns.map((col) => (col.sortValue ? col.sortValue(item) : ''));
        return { id, rowTitle, status, priority, extra, raw: item };
      })
      .filter((r) => !!r.id)
      .filter((r) => !hiddenStatuses.has(r.status))
      .filter((r) => extraFilters.every((f) => !f.predicate || f.predicate(r.raw)))
      .filter((r) =>
        !q ||
        r.id.toLowerCase().includes(q) ||
        r.rowTitle.toLowerCase().includes(q) ||
        r.status.toLowerCase().includes(q) ||
        r.priority.toLowerCase().includes(q)
      );
  }, [items, idField, titleField, statusField, priorityField, extraColumns, extraFilters, search, hiddenStatuses]);

  const { sorted: rows, field: sortField, dir: sortDir, toggle: handleSort } = useSortableData(
    filteredRows,
    (r, field: SortField) => (typeof field === 'number' ? (r.extra[field] ?? '') : (r[field] || '')),
    'id',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(rows, 25);

  // Stat tiles — computed from ALL items (unfiltered). A caller can replace the
  // whole set via `statCards` when the built-in keyword matching doesn't suit its
  // status / priority vocabulary.
  const statTiles = useMemo<StatCard[]>(() => {
    if (statCards) return statCards(items);
    const tiles: StatCard[] = [
      { label: 'Total', value: items.length, icon: 'ri-list-check-2', tone: 'primary' },
    ];
    if (statusField) {
      tiles.push({
        label: 'Open / Active',
        value: items.filter((item) => {
          const s = getValue(item, statusField).toLowerCase();
          return s.includes('open') || s.includes('active') || s.includes('new');
        }).length,
        icon: 'ri-checkbox-circle-line',
        tone: 'success',
      });
    }
    if (priorityField) {
      tiles.push({
        label: 'High / Critical',
        value: items.filter((item) => {
          const p = getValue(item, priorityField).toLowerCase();
          return p.includes('critical') || p.includes('high');
        }).length,
        icon: 'ri-alert-line',
        tone: 'danger',
      });
    }
    if (statusField) {
      tiles.push({
        label: 'Closed',
        value: items.filter((item) => {
          const s = getValue(item, statusField).toLowerCase();
          return s.includes('closed') || s.includes('resolved') || s.includes('archived') || s.includes('done');
        }).length,
        icon: 'ri-check-double-line',
        tone: 'neutral',
      });
    }
    return tiles;
  }, [items, statusField, priorityField, statCards]);

  // The built-in tile set has always been laid out on a 4-column grid however
  // many tiles it produced — keep that exactly so existing callers don't shift.
  // Custom sets size themselves (np-grid-1 doesn't exist, so clamp to 2..5).
  const statGridClass = statCards
    ? `np-grid-${Math.min(Math.max(statTiles.length, 2), 5)}`
    : 'np-grid-4';

  const renderStatusChip = (status: string): React.ReactNode =>
    statusChip
      ? statusChip(status)
      : <span className={`np-chip np-tone-${statusTone(status)}`}>{status}</span>;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const endpoint = `${listPath}/${encodeURIComponent(id)}`;
      return api.delete<any>(endpoint);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['list', listPath, registerId] });
      toast.success('Item deleted.');
    },
    onError: (e: Error) => {
      toast.error(`Failed to delete: ${e.message}`);
    },
  });

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon={iconClass}
        title={title}
        actions={
          <>
            <Link
              className="np-btn np-btn-primary"
              to={registerId ? `${detailPathPrefix}/new?${registerQueryParam}=${encodeURIComponent(registerId)}` : `${detailPathPrefix}/new`}
            >
              <i className="ri-add-line"></i>New
            </Link>
            {headerActions}
            {registersPath && (
              <Link className="np-btn np-btn-ghost" to={registersPath}>
                <i className="ri-folder-settings-line"></i>{registersButtonLabel}
              </Link>
            )}
            {configPath && registerId && (
              <Link className="np-btn np-btn-ghost" to={`${configPath}?registerId=${encodeURIComponent(registerId)}`}>
                <i className="ri-settings-3-line"></i>{configButtonLabel}
              </Link>
            )}
            <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
              <i className="ri-arrow-left-line"></i>Back
            </button>
          </>
        }
      />

      {!isLoading && items.length > 0 && statTiles.length > 0 && (
        <div className={`np-grid ${statGridClass}`}>
          {statTiles.map((tile) => (
            <div className="np-card" key={tile.label}>
              <div className="np-card-body np-stat">
                <span className={`np-stat-icon np-tone-${tile.tone || 'primary'}`}><i className={tile.icon}></i></span>
                <p className="np-stat-label">{tile.label}</p>
                <p className="np-stat-value np-tabular">{tile.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!registerId && registersPath && (
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <span>
            Showing {title.toLowerCase()} from all registers. To add new entries, open a specific register from{' '}
            <Link to={registersPath}>{registersButtonLabel}</Link>.
          </span>
        </div>
      )}

      <div className="np-toolbar">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="np-search" style={{ maxWidth: 280 }}>
            <i className="ri-search-line"></i>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="np-icon-btn" onClick={() => setSearch('')}>
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>

          {statusField && availableStatuses.length > 0 && (
            <div className="position-relative">
              <button
                type="button"
                className="np-filter-pill d-flex align-items-center gap-1"
                onClick={() => setStatusFilterOpen((o) => !o)}
              >
                <i className="ri-filter-3-line"></i>
                Status
                {hiddenStatuses.size > 0 && (
                  <span className="np-chip np-tone-primary">
                    {availableStatuses.length - hiddenStatuses.size}/{availableStatuses.length}
                  </span>
                )}
              </button>
              {statusFilterOpen && (
                <>
                  {/* Full-screen click-catcher to close the dropdown on outside click -
                      avoids depending on Bootstrap's own JS dropdown behaviour. */}
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                    onClick={() => setStatusFilterOpen(false)}
                  />
                  <div className="np-popover">
                    <div className="np-popover-head">
                      <span>Show status</span>
                      {hiddenStatuses.size > 0 && (
                        <button type="button" onClick={() => setHiddenStatuses(new Set())}>
                          Show all
                        </button>
                      )}
                    </div>
                    {availableStatuses.map((s) => (
                      <label key={s} className="np-popover-item">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked={!hiddenStatuses.has(s)}
                          onChange={() => toggleHiddenStatus(s)}
                        />
                        {renderStatusChip(s)}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {extraFilters.map((f) => (
            <React.Fragment key={f.id}>{f.control}</React.Fragment>
          ))}
        </div>
        <small className="np-sub np-mb-0">
          {rows.length} {rows.length === 1 ? 'item' : 'items'}{search ? ' matching' : ''}
        </small>
      </div>
      <div className="np-card">
        <div className="np-card-body p-0">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger m-3"><i className="ri-error-warning-line"></i>Failed to load {title.toLowerCase()}.</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-inbox-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">
                {search ? `No results for "${search}"` : `No ${title.toLowerCase()} found.`}
              </p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                    <SortableTh field="id" active={sortField === 'id'} dir={sortDir} onSort={handleSort} width="140px">ID</SortableTh>
                    <SortableTh field="rowTitle" active={sortField === 'rowTitle'} dir={sortDir} onSort={handleSort}>Title</SortableTh>
                    {priorityField && (
                      <SortableTh field="priority" active={sortField === 'priority'} dir={sortDir} onSort={handleSort} width="160px">Priority</SortableTh>
                    )}
                    {statusField && (
                      <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={handleSort} width="160px">Status</SortableTh>
                    )}
                    {extraColumns.map((col, i) =>
                      col.sortValue ? (
                        <SortableTh key={col.header} field={i} active={sortField === i} dir={sortDir} onSort={handleSort} width={col.width}>{col.header}</SortableTh>
                      ) : (
                        <th key={col.header} style={{ width: col.width }}>{col.header}</th>
                      )
                    )}
                    <th style={{ width: '110px' }}></th>
                  </tr>}>
                  {pageItems.map((r) => (
                    <tr key={r.id}>
                      <td><code className="text-muted small">{r.id}</code></td>
                      <td className="np-row-title-static">{r.rowTitle}</td>
                      {priorityField && (
                        <td>
                          {r.priority
                            ? <span className={`np-chip np-tone-${priorityTone(r.priority)}`}>{r.priority}</span>
                            : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                        </td>
                      )}
                      {statusField && (
                        <td>
                          {r.status
                            ? renderStatusChip(r.status)
                            : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                        </td>
                      )}
                      {extraColumns.map((col) => (
                        <td key={col.header}>{col.render(r.raw)}</td>
                      ))}
                      <td>
                        <div className="d-flex gap-1">
                          <Link
                            className="np-icon-btn"
                            to={`${detailPathPrefix}/${encodeURIComponent(r.id)}?${new URLSearchParams({
                              ...(registerId ? { [registerQueryParam]: registerId } : {}),
                              edit: 'true',
                            }).toString()}`}
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </Link>
                          {rowActions && rowActions(r.id, r.rowTitle, registerId)}
                          <button
                            type="button"
                            className="np-icon-btn np-icon-btn-danger"
                            onClick={async () => {
                              const confirmed = await swal.confirmDelete(r.rowTitle);
                              if (confirmed) deleteMutation.mutate(r.id);
                            }}
                            title="Delete"
                            disabled={deleteMutation.isPending}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </NimbusTable>
          )}
          {rows.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>
    </div>
  );
};
